let isInstalled = false;
const inFlightRequests = new Map<string, Promise<Response>>();

const DEDUPABLE_METHODS = new Set(["GET", "HEAD", "OPTIONS", "POST"]);

const getHeaderValue = (
  headers: HeadersInit | undefined,
  key: string,
): string | null => {
  if (!headers) return null;

  if (headers instanceof Headers) {
    return headers.get(key);
  }

  if (Array.isArray(headers)) {
    const match = headers.find(([headerKey]) =>
      headerKey.toLowerCase() === key.toLowerCase(),
    );
    return match ? match[1] : null;
  }

  const normalizedKey = Object.keys(headers).find(
    (headerKey) => headerKey.toLowerCase() === key.toLowerCase(),
  );

  if (!normalizedKey) return null;

  const value = (headers as Record<string, string>)[normalizedKey];
  return value ?? null;
};

const getRequestBodyFingerprint = (
  body: BodyInit | null | undefined,
): string => {
  if (!body) return "";

  if (typeof body === "string") {
    return body;
  }

  if (body instanceof URLSearchParams) {
    return body.toString();
  }

  if (typeof FormData !== "undefined" && body instanceof FormData) {
    const entries: string[] = [];

    body.forEach((value, key) => {
      entries.push(`${key}:${typeof value === "string" ? value : "[file]"}`);
    });

    return entries.sort().join("&");
  }

  return "[non-string-body]";
};

const resolveRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

const buildRequestKey = (input: RequestInfo | URL, init?: RequestInit): string => {
  const url = resolveRequestUrl(input);
  const method = (init?.method ?? (input instanceof Request ? input.method : "GET")).toUpperCase();
  const bodyFingerprint = getRequestBodyFingerprint(init?.body);

  return `${method}|${url}|${bodyFingerprint}`;
};

export const installFetchInFlightDedupe = (): void => {
  if (isInstalled || typeof window === "undefined") {
    return;
  }

  const nativeFetch = window.fetch.bind(window);

  window.fetch = (async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    ).toUpperCase();

    const bypassHeader = getHeaderValue(init?.headers, "x-skip-request-dedupe");

    if (!DEDUPABLE_METHODS.has(method) || bypassHeader === "1") {
      return nativeFetch(input, init);
    }

    const requestKey = buildRequestKey(input, init);
    const existingRequest = inFlightRequests.get(requestKey);

    if (existingRequest) {
      return existingRequest.then((response) => response.clone());
    }

    const requestPromise = nativeFetch(input, init);
    inFlightRequests.set(requestKey, requestPromise);

    try {
      const response = await requestPromise;
      return response.clone();
    } finally {
      inFlightRequests.delete(requestKey);
    }
  }) as typeof window.fetch;

  isInstalled = true;
};
